import React, { useState } from 'react';
import { cn } from "@/lib/utils";
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { router, AppRoute } from '@/lib/router';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useBetaMode } from '@/hooks/useBetaMode';
import { useTranslation } from '@/contexts/I18nContext';

interface RegisterFormProps {
  onSwitchToLogin?: () => void;
  onSwitchToForgotPassword?: () => void;
  className?: string;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({
  onSwitchToLogin,
  className,
  ...props
}) => {
  const { register, sendOtp } = useAuth();
  const { betaMode, loading: betaLoading } = useBetaMode();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    verificationCode: '',
    invitationCode: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<'info' | 'verification'>('info');
  // const [setIsCodeSent] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSendCode = async () => {
    if (!formData.email) {
      toast.error(t('auth.pleaseEnterEmail'));
      return;
    }

    setIsLoading(true);

    try {
      await sendOtp(formData.email, 0, 'registration', undefined, formData.invitationCode); // isLogin=0, purpose='registration'
      // setIsCodeSent(true);
      setStep('verification');
      toast.success(t('auth.verificationCodeSent'));
    } catch (err: any) {
      toast.error(err.message || t('auth.failedToSendCode'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (step === 'info') {
      // 验证表单
      if (formData.password !== formData.confirmPassword) {
        toast.error(t('auth.passwordsDoNotMatch'));
        return;
      }
      if (formData.password.length < 8) {
        toast.error(t('auth.passwordTooShort'));
        return;
      }

      await handleSendCode();
      return;
    }

    // 注册步骤
    setIsLoading(true);

    try {
      // 在内测模式下，需要传递邀请码
      if (betaMode && !formData.invitationCode) {
        toast.error(t('auth.invitationCodeRequiredError'));
        return;
      }

      await register(
        formData.email,
        formData.password,
        formData.name,
        formData.verificationCode,
        undefined, // turnstileToken
        formData.invitationCode || undefined
      );
      toast.success(t('auth.registrationSuccessful'));
      setTimeout(() => {
        router.navigate(AppRoute.HOME);
      }, 1500);
    } catch (err: any) {
      toast.error(err.message || t('auth.registrationFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  // const handleSocialLogin = async (provider: 'apple' | 'google') => {
  //   try {
  //     // 模拟社交登录数据（实际应用中这些数据来自社交登录SDK）
  //     const mockAuth0User = {
  //       sub: `${provider}|mock_user_id_${Date.now()}`,
  //       email: `user@${provider}.com`,
  //       name: `${provider.charAt(0).toUpperCase() + provider.slice(1)} User`,
  //       nickname: `${provider}user`,
  //       picture: `https://example.com/${provider}_avatar.jpg`,
  //       email_verified: true
  //     };

  //     await socialLogin(mockAuth0User);
  //     router.navigate(AppRoute.HOME);
  //   } catch (error) {
  //     console.error(`${provider} login failed:`, error);
  //     toast.error(`${provider.charAt(0).toUpperCase() + provider.slice(1)} login failed`);
  //   }
  // };

  const isInfoFormValid = formData.email &&
    formData.password &&
    formData.confirmPassword &&
    formData.name &&
    (!betaMode || formData.invitationCode); // 内测模式下需要邀请码
  const isVerificationFormValid = formData.verificationCode;

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card className="overflow-hidden p-0">
        <CardContent className="grid p-0 md:grid-cols-2">
          <form onSubmit={handleSubmit} className="p-6 md:p-8">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col items-center text-center">
                <img src="/icons/logo.png" alt="Logo" className="w-12 h-12 mb-4" />
                <h1 className="text-2xl font-bold">
                  {step === 'info' ? t('auth.createAccount') : t('auth.verifyYourEmail')}
                </h1>
                <p className="text-muted-foreground text-balance">
                  {step === 'info'
                    ? t('auth.signUpWithSocialOrEmail')
                    : t('auth.enterVerificationCode')
                  }
                </p>
              </div>

              {step === 'info' && (
                <>
                  {/* 社交登录按钮 */}
                  {/* <div className="grid grid-cols-2 gap-4">
                    <Button 
                      variant="outline" 
                      type="button" 
                      className="w-full"
                      onClick={() => handleSocialLogin('apple')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="mr-2 h-4 w-4">
                        <path
                          d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"
                          fill="currentColor"
                        />
                      </svg>
                      Apple
                    </Button>
                    <Button 
                      variant="outline" 
                      type="button" 
                      className="w-full"
                      onClick={() => handleSocialLogin('google')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="mr-2 h-4 w-4">
                        <path
                          d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                          fill="currentColor"
                        />
                      </svg>
                      Google
                    </Button>
                  </div> */}

                  {/* <div className="after:border-border relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t">
                    <span className="bg-card text-muted-foreground relative z-10 px-2">
                      Or continue with
                    </span>
                  </div> */}

                  {/* 姓名输入 */}
                  <div className="grid gap-3">
                    <Label htmlFor="name">{t('auth.fullName')}</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder={t('auth.fullNamePlaceholder')}
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      disabled={isLoading}
                    />
                  </div>

                  {/* 邮箱输入 */}
                  <div className="grid gap-3">
                    <Label htmlFor="email">{t('auth.email')}</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder={t('auth.emailPlaceholder')}
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      disabled={isLoading}
                    />
                  </div>

                  {/* 密码输入 */}
                  <div className="grid gap-3">
                    <Label htmlFor="password">{t('auth.password')}</Label>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      placeholder={t('auth.passwordPlaceholder')}
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      disabled={isLoading}
                    />
                  </div>

                  {/* 确认密码输入 */}
                  <div className="grid gap-3">
                    <Label htmlFor="confirmPassword">{t('auth.confirmPassword')}</Label>
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      placeholder={t('auth.confirmPasswordPlaceholder')}
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      required
                      disabled={isLoading}
                    />
                  </div>

                  {/* 内测模式下显示邀请码输入框 */}
                  {!betaLoading && betaMode && (
                    <div className="grid gap-3">
                      <Label htmlFor="invitationCode">
                        {t('auth.invitationCode')}
                        <span className="text-red-500 ml-1">*</span>
                      </Label>
                      <Input
                        id="invitationCode"
                        name="invitationCode"
                        type="text"
                        placeholder={t('auth.invitationCodePlaceholder')}
                        value={formData.invitationCode}
                        onChange={handleInputChange}
                        maxLength={4}
                        required
                        disabled={isLoading}
                        className="font-mono tracking-wider"
                      />
                      <p className="text-xs text-muted-foreground">
                        {t('auth.invitationCodeDescription')}
                      </p>
                    </div>
                  )}
                </>
              )}

              {step === 'verification' && (
                <>
                  {/* 验证码输入 */}
                  <div className="grid gap-3">
                    <Label htmlFor="verificationCode">{t('auth.verificationCode')}</Label>
                    <Input
                      id="verificationCode"
                      name="verificationCode"
                      type="text"
                      placeholder={t('auth.verificationCodePlaceholder')}
                      value={formData.verificationCode}
                      onChange={handleInputChange}
                      maxLength={6}
                      required
                      disabled={isLoading}
                    />
                    <p className="text-xs text-muted-foreground">
                      {t('auth.codeSentTo').replace('{email}', formData.email)}
                    </p>
                  </div>

                  {/* 重新发送验证码 */}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleSendCode}
                    disabled={isLoading}
                  >
                    {t('auth.resendVerificationCode')}
                  </Button>
                </>
              )}

              {/* 提交按钮 */}
              <Button
                type="submit"
                className="w-full"
                disabled={
                  (step === 'info' && !isInfoFormValid) ||
                  (step === 'verification' && !isVerificationFormValid) ||
                  isLoading
                }
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {step === 'info' ? t('auth.sendingCode') : t('auth.creatingAccount')}
                  </div>
                ) : (
                  step === 'info' ? t('auth.sendVerificationCode') : t('auth.createAccountButton')
                )}
              </Button>

              {/* 返回登录 */}
              {step === 'info' && (
                <div className="text-center text-sm">
                  {t('auth.alreadyHaveAccount')}{" "}
                  <button
                    type="button"
                    onClick={onSwitchToLogin}
                    className="underline underline-offset-4"
                  >
                    {t('auth.signIn')}
                  </button>
                </div>
              )}

              {/* 返回上一步 */}
              {step === 'verification' && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setStep('info')}
                    className="text-sm underline underline-offset-4"
                  >
                    {t('auth.backToPreviousStep')}
                  </button>
                </div>
              )}
            </div>
          </form>
          <div className="bg-muted relative hidden md:block">
            <img
              src="/imgs/login-cover.avif"
              alt="Registration illustration"
              className="absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale"
            />
          </div>
        </CardContent>
      </Card>
      <div className="text-muted-foreground text-center text-xs text-balance">
        {t('auth.termsAndPrivacy')}{" "}
        <a href="#" className="underline underline-offset-4 hover:text-primary">
          {t('auth.termsOfService')}
        </a>{" "}
        {t('auth.and')}{" "}
        <a href="#" className="underline underline-offset-4 hover:text-primary">
          {t('auth.privacyPolicy')}
        </a>.
      </div>
    </div>
  );
}; 